import React, { useEffect, useState, useMemo } from "react";
import MainLayout from "../../components/Layouts/MainLayout";
import { useSelector } from "react-redux";
import ChartJSCard from "../../components/Dashboard/ChartJSCard";
import apiClient from '../../api/apiClient';

const AdminDashboard = () => {
  const [data, setData] = useState({});
  const [taskersData, setTaskersData] = useState([]);
  const [tasksOverviewData, setTasksOverviewData] = useState({});
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    const handleDarkModeChange = (e) => {
      setIsDarkMode(e.matches);
    };

    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", handleDarkModeChange);

    return () => {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", handleDarkModeChange);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(
          `/api/dashboard/overview/`
        );
        setData(response.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTaskersData = async () => {
      try {
        const response = await apiClient.get(
          `/api/dashboard/top-10-taskers-taskers/`
        );
        setTaskersData(response.data);
      } catch (error) {
        console.error("Error fetching taskers data:", error);
      }
    };

    const fetchTasksOverviewData = async () => {
      try {
        const response = await apiClient.get(
          `/api/dashboard/tasks-overview/`
        );
        setTasksOverviewData(response.data);
      } catch (error) {
        console.error("Error fetching tasks overview data:", error);
      }
    };

    fetchData();
    fetchTaskersData();
    fetchTasksOverviewData();
  }, []);

  // Time of day greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Create ChartJS data for doughnut chart
  const donutChartData = useMemo(() => ({
    labels: ['Total Tasks', 'Accepted Tasks', 'Rejected Tasks', 'Pending Tasks'],
    datasets: [
      {
        data: [
          tasksOverviewData?.total_tasks || 0,
          tasksOverviewData?.total_tasks_accepted || 0,
          tasksOverviewData?.total_tasks_rejected || 0,
          tasksOverviewData?.total_tasks_pending || 0,
        ],
        backgroundColor: [
          '#00E396',
          '#008FFB',
          '#FF4560',
          '#FEB019',
    ],
        borderColor: isDarkMode ? '#1F2937' : '#FFFFFF',
        borderWidth: 2,
      },
    ],
  }), [isDarkMode, tasksOverviewData]);

  // Create ChartJS data for bar chart
  const barChartData = useMemo(() => ({
    labels: taskersData.map(
      (tasker) => `${tasker.first_name || "User"} ${tasker.last_name || tasker.id}`
      ),
    datasets: [
      {
        label: 'Total Tasks',
        data: taskersData.map((tasker) => tasker.total_tasks),
        backgroundColor: '#00E396',
        borderColor: isDarkMode ? '#1F2937' : '#FFFFFF',
        borderWidth: 1,
      },
      {
        label: 'Total Reviews',
        data: taskersData.map((tasker) => tasker.total_reviews),
        backgroundColor: '#008FFB',
        borderColor: isDarkMode ? '#1F2937' : '#FFFFFF',
        borderWidth: 1,
      },
    ],
  }), [isDarkMode, taskersData]);

  return (
    <MainLayout>
      <main className="p-4 md:ml-64 min-h-screen pt-10 bg-gradient-to-br dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto sm:px-6 py-10 mb-5">
          {/* Header Section with Animated Greeting */}
          <div className="px-4 sm:px-0 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <span className="inline-block px-3 py-1 text-sm font-medium text-emerald-800 bg-emerald-100 rounded-full dark:bg-emerald-900 dark:text-emerald-200">
                  {getGreeting()}
                </span>
                <h1 className="mt-2 text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white">
                  Hello, <span className="text-emerald-600 dark:text-emerald-400">{user?.first_name}</span>!
                </h1>
                <p className="mt-1 text-gray-600 dark:text-gray-300">
                  Your admin dashboard overview
                </p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="px-4 sm:px-0 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Tasks Attempted Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6 relative overflow-hidden">
                  <div className="absolute right-0 top-0 w-20 h-20 opacity-10">
                    <svg className="text-emerald-600 dark:text-emerald-400 w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Total Tasks Attempted
                  </h3>
                  {isLoading ? (
                    <div className="animate-pulse h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  ) : (
                    <div className="flex items-end">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">{data?.total_tasks || 0}</span>
                      <div className="ml-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        <span className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                          </svg>
                          Today: {data?.attempted_today || 0}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Total Tasks Reviewed Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6 relative overflow-hidden">
                  <div className="absolute right-0 top-0 w-20 h-20 opacity-10">
                    <svg className="text-cyan-600 dark:text-cyan-400 w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Total Tasks Reviewed
                  </h3>
                  {isLoading ? (
                    <div className="animate-pulse h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  ) : (
                    <div className="flex items-end">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">{data?.total_tasks_reviewed || 0}</span>
                      <div className="ml-2 text-sm font-medium text-cyan-600 dark:text-cyan-400">
                        <span className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                          </svg>
                          Today: {data?.reviewed_today || 0}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Active Users Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6 relative overflow-hidden">
                  <div className="absolute right-0 top-0 w-20 h-20 opacity-10">
                    <svg className="text-purple-600 dark:text-purple-400 w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Users Active Today
                  </h3>
                  {isLoading ? (
                    <div className="animate-pulse h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  ) : (
                    <div className="flex items-end">
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">{data?.users_active_today || 0}</span>
                      <div className="ml-2 text-sm font-medium text-purple-600 dark:text-purple-400">
                        <span className="flex items-center">Active</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Accepted/Rejected Ratio Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6 relative overflow-hidden">
                  <div className="absolute right-0 top-0 w-20 h-20 opacity-10">
                    <svg className="text-green-600 dark:text-green-400 w-full h-full" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Task Acceptance Ratio
                  </h3>
                  {isLoading ? (
                    <div className="animate-pulse h-8 w-16 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  ) : (
                    <div>
                      <div className="flex items-end">
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">{data?.accepted_tasks_ratio || 0}%</span>
                        <div className="ml-2 text-sm font-medium text-green-600 dark:text-green-400">
                          <span className="flex items-center">Accepted</span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-end">
                        <span className="text-lg font-medium text-gray-700 dark:text-gray-300">{data?.rejected_tasks_ratio || 0}%</span>
                        <div className="ml-2 text-sm font-medium text-red-600 dark:text-red-400">
                          <span className="flex items-center">Rejected</span>
                        </div>
                      </div>
                      <div className="mt-3 w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                        <div 
                          className="bg-green-500 h-1.5 rounded-full" 
                          style={{ width: `${data?.accepted_tasks_ratio || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="px-4 sm:px-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">Task Overview</h2>
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                      Distribution
                    </span>
                  </div>
                  {isLoading ? (
                    <div className="animate-pulse bg-gray-200 dark:bg-gray-600 rounded-lg h-64"></div>
                  ) : (
                    <ChartJSCard 
                      type="doughnut"
                      data={donutChartData}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">Top 10 Attempters</h2>
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200">
                      Performance
                    </span>
                  </div>
                  {isLoading ? (
                    <div className="animate-pulse bg-gray-200 dark:bg-gray-600 rounded-lg h-64"></div>
                  ) : (
                    <ChartJSCard 
                      type="bar"
                      data={barChartData}
                      isDarkMode={isDarkMode}
            />
                  )}
                </div>
          </div>
        </div>
          </div>
        </div>
      </main>
    </MainLayout>
  );
};

export default AdminDashboard;